import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchTopShelf } from '../../phoenix/data/topShelf';
import { useCategoryTreeItem } from '../categoryTree';
import { useRefetchQuery } from '../hooks/useRefetchQuery';
import { queryKeys } from '../queryKeys';

type UseTopShelfProps = {
  referenceId: string;
};

export function useTopShelf({ referenceId }: UseTopShelfProps) {
  const category = useCategoryTreeItem(referenceId);
  const enabled = Boolean(category);

  const {
    data,
    error,
    isFetching,
    isLoading: isDataLoading,
    isPending,
    refetch: refetchTopShelf,
  } = useQuery({
    queryKey: queryKeys.topShelf(referenceId),
    queryFn: () =>
      fetchTopShelf({
        collectionId: category!.channels[0],
        pageSize: 50,
        pageIndex: 1,
        groupBy: category!.groupBy,
        kSql: category!.aggregatedKSQL,
      }),
    enabled,
  });

  const isLoading = isDataLoading || isFetching || isPending;

  const refetch = useRefetchQuery({ enabled, refetch: refetchTopShelf });

  return useMemo(() => {
    if (!data) {
      return {
        error,
        items: [],
        totalCount: 0,
        isLoading,
        refetch,
      };
    }

    return {
      items: data.objects,
      error,
      totalCount: data.totalCount,
      isLoading,
      refetch,
    };
  }, [data, isLoading, error, refetch]);
}
